import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * Reusable confirmation dialog component
 *
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Function to call when dialog closes
 * @param {function} onConfirm - Function to call when user confirms
 * @param {string} title - Dialog title
 * @param {string} message - Main confirmation message
 * @param {string} warning - Additional warning message (optional)
 * @param {string} severity - Alert severity: 'warning' | 'error' | 'info' (default: 'warning')
 * @param {boolean} loading - Whether action is in progress
 * @param {string} confirmText - Text for confirm button (default: 'Delete')
 * @param {string} cancelText - Text for cancel button (default: 'Cancel')
 * @param {string} confirmColor - Color for confirm button (default: 'error')
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  warning = null,
  severity = 'warning',
  loading = false,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  confirmColor = 'error'
}) => {
  return (
    <Dialog
      open={open}
      onClose={loading ? null : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {severity === 'error' ? (
            <ErrorIcon color="error" />
          ) : (
            <WarningIcon color="warning" />
          )}
          {title}
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: warning ? 2 : 0 }}>
          {message}
        </DialogContentText>

        {warning && (
          <Alert severity={severity} sx={{ mt: 2 }}>
            {warning}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
